import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { tripService } from '@/services/cart.service';
import { useCart } from '@/context/CartContext';
import { mediaUrl, formatApiError } from '@/lib/api';
import { PublicLayout } from '@/components/public/PublicLayout';
import { TripEarthMap } from '@/components/public/TripEarthMap';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  Sparkles, MapPin, Calendar, Users, Baby, Wand2, Loader2, Plus, Check, BedDouble,
  Clock, ArrowRight, Minus, Wallet, Compass, Mountain, Leaf, HeartPulse, GraduationCap,
  UtensilsCrossed, Camera, Globe, ShoppingCart, Star, ShieldCheck, RefreshCw, Share2, Flame,
} from 'lucide-react';

const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (iso, days) => { const d = iso ? new Date(iso) : new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
const fmtD = (iso) => { try { return new Date(`${iso}T00:00:00`).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' }); } catch { return iso; } };

const INTERESTS = [
  { key: 'Natura', icon: Leaf },
  { key: 'Trekking', icon: Mountain },
  { key: 'Benessere', icon: HeartPulse },
  { key: 'Corso', icon: GraduationCap },
  { key: 'Gusto', icon: UtensilsCrossed },
  { key: 'Cultura', icon: Camera },
  { key: 'Famiglia', icon: Baby },
  { key: 'Avventura', icon: Compass },
];

function Stepper({ value, setValue, min = 0, max = 20, label, icon: Icon, id }) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">{Icon && <Icon className="h-4 w-4 text-muted-foreground" />}{label}</label>
      <div className="flex items-center justify-between rounded-md border border-input bg-background px-2 py-1.5">
        <button type="button" aria-label="Diminuisci" onClick={() => setValue(Math.max(min, value - 1))} disabled={value <= min}
          data-testid={`${id}-minus`} className="flex h-7 w-7 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent disabled:opacity-40">
          <Minus className="h-4 w-4" />
        </button>
        <span className="min-w-[2ch] text-center text-sm font-semibold tabular-nums" data-testid={`${id}-value`}>{value}</span>
        <button type="button" aria-label="Aumenta" onClick={() => setValue(Math.min(max, value + 1))} disabled={value >= max}
          data-testid={`${id}-plus`} className="flex h-7 w-7 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent disabled:opacity-40">
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function TripPlanner() {
  const navigate = useNavigate();
  const { token } = useParams();
  const shared = !!token;
  const { isCustomer, addStay, addItem, preview, cart, openMiniCart } = useCart() || {};

  const [destination, setDestination] = useState('Sila');
  const [checkin, setCheckin] = useState(plusDaysISO(todayISO(), 7));
  const [checkout, setCheckout] = useState(plusDaysISO(todayISO(), 9));
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [interests, setInterests] = useState(['Natura']);
  const [pace, setPace] = useState('rilassato');
  const [budget, setBudget] = useState('');

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [addingAll, setAddingAll] = useState(false);
  const [stayIdx, setStayIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sharedTitle, setSharedTitle] = useState('');

  // Modalità condivisa: carica lo snapshot dal token
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    tripService.getShared(token)
      .then((res) => { setPlan(res?.plan || null); setSharedTitle(res?.title || ''); })
      .catch(() => toast.error('Viaggio non trovato o link scaduto'))
      .finally(() => setLoading(false));
  }, [token]);

  const toggleInterest = (k) =>
    setInterests((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const generate = async () => {
    setLoading(true);
    setPlan(null);
    setStayIdx(0);
    try {
      const res = await tripService.plan({
        destination, date_from: checkin, date_to: checkout,
        adults, children, interests, pace, budget: budget ? Number(budget) : null,
      });
      setPlan(res);
      if (!res?.stay && (res?.experiences_count || 0) === 0) {
        toast.info('Nessuna proposta per questi criteri. Prova a cambiare destinazione o interessi.');
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || 'Errore nella generazione');
    } finally {
      setLoading(false);
    }
  };

  const stays = useMemo(() => (plan ? [plan.stay, ...(plan.stay_alternatives || [])].filter(Boolean) : []), [plan]);
  const selectedStay = stays[stayIdx] || null;
  const allExps = useMemo(() => (plan?.days || []).flatMap((d) => d.items), [plan]);

  const estimatedTotal = useMemo(() => {
    const s = selectedStay ? Number(selectedStay.total || 0) : 0;
    const e = allExps.reduce((acc, x) => acc + Number(x.price || 0), 0);
    return s + e;
  }, [selectedStay, allExps]);

  // Sconto pacchetto (config Admin CRM) — percentuali PER MODULO
  const bStay = Number(plan?.bundle?.pct_stay || 0);
  const bExp = Number(plan?.bundle?.pct_experience || 0);
  const bundleEligible = !!plan?.bundle?.enabled && !!selectedStay
    && allExps.length >= Number(plan?.bundle?.min_experiences || 1) && (bStay > 0 || bExp > 0);
  const stayTotal = selectedStay ? Number(selectedStay.total || 0) : 0;
  const expSum = allExps.reduce((acc, x) => acc + Number(x.price || 0), 0);
  const bundleDiscount = bundleEligible ? Math.round((stayTotal * bStay / 100 + expSum * bExp / 100) * 100) / 100 : 0;
  const bundlePct = bundleEligible && estimatedTotal > 0 ? Math.round((bundleDiscount / estimatedTotal) * 100) : 0;
  const totalAfter = Math.round((estimatedTotal - bundleDiscount) * 100) / 100;

  const requireLogin = () => {
    toast.info('Accedi come cliente per salvare il viaggio nel carrello');
    navigate('/cliente/accedi');
  };

  const addStayToTrip = async (stay) => {
    if (!stay) return;
    if (!isCustomer) return requireLogin();
    setBusy(`stay:${stay.room_id}`);
    try {
      await addStay({
        owner_id: stay.owner_id, room_id: stay.room_id,
        check_in: stay.check_in, check_out: stay.check_out,
        adults: stay.adults, children: stay.children,
      });
      toast.success(`Soggiorno "${stay.structure_name}" aggiunto al viaggio`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || 'Impossibile aggiungere il soggiorno');
    } finally { setBusy(null); }
  };

  const addExp = async (exp) => {
    if (!isCustomer) return requireLogin();
    setBusy(exp.catalog_id);
    try {
      await addItem(exp.catalog_id, 1);
      toast.success(`"${exp.title}" aggiunta al viaggio`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || 'Impossibile aggiungere');
    } finally { setBusy(null); }
  };

  const addAll = async () => {
    if (!isCustomer) return requireLogin();
    setAddingAll(true);
    let ok = 0; let fail = 0;
    try {
      if (selectedStay) {
        try {
          await addStay({
            owner_id: selectedStay.owner_id, room_id: selectedStay.room_id,
            check_in: selectedStay.check_in, check_out: selectedStay.check_out,
            adults: selectedStay.adults, children: selectedStay.children,
          });
          ok += 1;
        } catch { fail += 1; }
      }
      for (const exp of allExps) {
        try { await addItem(exp.catalog_id, 1); ok += 1; } catch { fail += 1; }
      }
      if (ok > 0) {
        toast.success(`${ok} voci aggiunte al tuo viaggio${fail ? ` · ${fail} non disponibili` : ''}`);
        openMiniCart && openMiniCart();
      } else {
        toast.error('Nessuna voce aggiunta. Riprova.');
      }
    } finally { setAddingAll(false); }
  };

  const saveShare = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      const res = await tripService.save(plan);
      const url = `${window.location.origin}${res.url_path}`;
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard non disponibile */ }
      toast.success('Link del viaggio copiato negli appunti!', { description: url });
    } catch (e) {
      toast.error('Impossibile salvare il viaggio');
    } finally { setSaving(false); }
  };

  const cartItems = cart?.items || [];
  const cartCount = cartItems.reduce((s, it) => s + Number(it.qty || 0), 0);

  return (
    <PublicLayout>
      <Seo title="Componi la tua vacanza · Sila Experience" description="Crea il tuo viaggio su misura in Sila: soggiorno, esperienze e sapori locali in un unico itinerario e un solo checkout." />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10" style={{ background: 'radial-gradient(1100px 520px at 12% 0%, hsla(var(--brand-lake)/0.18) 0%, transparent 60%), radial-gradient(900px 480px at 90% 15%, hsla(var(--brand-sun)/0.16) 0%, transparent 55%)' }} />
        <div className="sila-section py-12 sm:py-16">
          <Badge variant="secondary" className="gap-1.5 rounded-full">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> {shared ? 'Itinerario condiviso' : 'Trip Builder'}
          </Badge>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold tracking-tight sm:text-5xl">
            {shared ? `Un viaggio in ${sharedTitle || 'Sila'} per te` : 'Componi la tua vacanza in Sila'}
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
            {shared
              ? 'Ecco l\u2019itinerario che ti è stato condiviso. Personalizzalo e aggiungilo al tuo viaggio.'
              : <>Soggiorno, esperienze e sapori locali in <strong>un solo itinerario</strong> e un unico checkout. Ti proponiamo un viaggio su misura: personalizzalo e aggiungilo al carrello.</>}
          </p>
        </div>
      </section>

      <div className="sila-section grid gap-8 pb-20 lg:grid-cols-[1fr_22rem]">
        {/* LEFT: composer + itinerary */}
        <div className="min-w-0 space-y-8">
          {/* COMPOSER */}
          {!shared && (
          <Card data-testid="trip-composer">
            <CardContent className="space-y-5 p-5 sm:p-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium">Destinazione</label>
                  <div className="relative">
                    <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input className="pl-9" value={destination} onChange={(e) => setDestination(e.target.value)}
                      placeholder="Es. Sila Grande, Camigliatello, Lorica…" data-testid="trip-destination" />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Check-in</label>
                  <div className="relative">
                    <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input type="date" min={todayISO()} value={checkin} className="pl-9" data-testid="trip-checkin"
                      onChange={(e) => { setCheckin(e.target.value); if (e.target.value >= checkout) setCheckout(plusDaysISO(e.target.value, 2)); }} />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Check-out</label>
                  <div className="relative">
                    <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input type="date" min={plusDaysISO(checkin, 1)} value={checkout} className="pl-9" data-testid="trip-checkout"
                      onChange={(e) => setCheckout(e.target.value)} />
                  </div>
                </div>
                <Stepper id="trip-adults" label="Adulti" icon={Users} value={adults} setValue={setAdults} min={1} max={16} />
                <Stepper id="trip-children" label="Bambini" icon={Baby} value={children} setValue={setChildren} min={0} max={10} />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">I tuoi interessi</label>
                <div className="flex flex-wrap gap-2" data-testid="trip-interests">
                  {INTERESTS.map(({ key, icon: Icon }) => {
                    const on = interests.includes(key);
                    return (
                      <button key={key} type="button" onClick={() => toggleInterest(key)} data-testid={`trip-interest-${key}`}
                        className={cn(
                          'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                          on ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent',
                        )}>
                        <Icon className="h-3.5 w-3.5" />{key}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">Ritmo del viaggio</label>
                  <div className="grid grid-cols-2 gap-1 rounded-2xl bg-muted/70 p-1" data-testid="trip-pace">
                    {[{ k: 'rilassato', l: 'Rilassato' }, { k: 'intenso', l: 'Intenso' }].map((o) => (
                      <button key={o.k} type="button" onClick={() => setPace(o.k)} data-testid={`trip-pace-${o.k}`}
                        className={cn('rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
                          pace === o.k ? 'bg-background text-primary shadow-[var(--shadow-sm)]' : 'text-muted-foreground hover:text-foreground')}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-2 flex items-center gap-1.5 text-sm font-medium"><Wallet className="h-4 w-4 text-muted-foreground" />Budget indicativo (facolt.)</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
                    <Input type="number" min={0} className="pl-7" value={budget} onChange={(e) => setBudget(e.target.value)}
                      placeholder="a persona / totale" data-testid="trip-budget" />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Button size="lg" className="rounded-full" onClick={generate} disabled={loading} data-testid="trip-generate">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
                  {loading ? 'Sto componendo…' : 'Genera itinerario'}
                </Button>
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="trip-ai-note">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  Presto potrai chiederlo a parole al nostro <strong>assistente AI</strong>.
                </p>
              </div>
            </CardContent>
          </Card>
          )}

          {/* RESULT */}
          {loading && (
            <div className="space-y-4" data-testid="trip-loading">
              <Skeleton className="h-40 w-full rounded-2xl" />
              <Skeleton className="h-32 w-full rounded-2xl" />
              <Skeleton className="h-32 w-full rounded-2xl" />
            </div>
          )}

          {!loading && plan && (
            <div className="space-y-6" data-testid="trip-result">
              {/* Summary bar */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3" data-testid="trip-summary">
                <span className="flex items-center gap-1.5 text-sm font-semibold"><MapPin className="h-4 w-4 text-primary" />{plan.intent.destination || 'Sila'}</span>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Calendar className="h-4 w-4" />{fmtD(plan.intent.date_from)} → {fmtD(plan.intent.date_to)}</span>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><BedDouble className="h-4 w-4" />{plan.nights} nott{plan.nights === 1 ? 'e' : 'i'}</span>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Users className="h-4 w-4" />{plan.intent.adults + plan.intent.children} ospiti</span>
                <span className="ml-auto flex items-center gap-2">
                  {bundleEligible && (
                    <Badge className="gap-1" data-testid="trip-bundle-badge"><Sparkles className="h-3 w-3" />Pacchetto -{bundlePct}%</Badge>
                  )}
                  <span className="text-xs text-muted-foreground">Totale stimato</span>
                  {bundleEligible && <span className="text-sm text-muted-foreground line-through tabular-nums">{eur(estimatedTotal)}</span>}
                  <span className="font-display text-xl font-bold tabular-nums" data-testid="trip-estimated-total">{eur(totalAfter)}</span>
                </span>
              </div>

              {/* Stay */}
              {selectedStay ? (
                <section className="space-y-3" data-testid="trip-stay-section">
                  <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><BedDouble className="h-5 w-5 text-primary" />Il tuo soggiorno</h2>
                  <Card className="overflow-hidden">
                    <div className="flex flex-col sm:flex-row">
                      <div className="h-40 w-full shrink-0 overflow-hidden bg-muted sm:h-auto sm:w-56">
                        {selectedStay.cover_image
                          ? <img src={mediaUrl(selectedStay.cover_image)} alt={selectedStay.structure_name} className="h-full w-full object-cover" />
                          : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><BedDouble className="h-8 w-8" /></div>}
                      </div>
                      <CardContent className="flex flex-1 flex-col gap-1.5 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-display text-lg font-semibold">{selectedStay.structure_name}</p>
                          {selectedStay.free_cancellation && <Badge variant="secondary" className="gap-1 text-[10px]"><ShieldCheck className="h-3 w-3" />Cancellazione gratuita</Badge>}
                          {selectedStay.breakfast_included && <Badge variant="secondary" className="text-[10px]">Colazione inclusa</Badge>}
                          {Number(selectedStay.rooms_left) > 0 && Number(selectedStay.rooms_left) <= 3 && (
                            <Badge className="gap-1 border-0 bg-[hsl(var(--brand-sun))]/20 text-[hsl(var(--brand-bark))] text-[10px]" data-testid="trip-urgency">
                              <Flame className="h-3 w-3" />Ultime {selectedStay.rooms_left} camere
                            </Badge>
                          )}
                        </div>
                        <p className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{selectedStay.address}</p>
                        <p className="text-sm text-muted-foreground">{selectedStay.room_name} · {selectedStay.nights} notti · {eur(selectedStay.per_night)}/notte</p>
                        <div className="mt-auto flex items-end justify-between pt-2">
                          <span className="font-display text-xl font-bold tabular-nums">{eur(selectedStay.total)}</span>
                          <Button size="sm" className="gap-1" disabled={busy === `stay:${selectedStay.room_id}`} onClick={() => addStayToTrip(selectedStay)} data-testid="trip-add-stay">
                            {busy === `stay:${selectedStay.room_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}Aggiungi soggiorno
                          </Button>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                  {stays.length > 1 && (
                    <div className="flex flex-wrap gap-2" data-testid="trip-stay-alternatives">
                      <span className="self-center text-xs text-muted-foreground">Alternative:</span>
                      {stays.map((s, i) => (
                        <button key={s.room_id} type="button" onClick={() => setStayIdx(i)} data-testid={`trip-stay-alt-${i}`}
                          className={cn('rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                            i === stayIdx ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent')}>
                          {s.structure_name} · {eur(s.total)}
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              ) : (
                <Card data-testid="trip-no-stay"><CardContent className="p-4 text-sm text-muted-foreground">
                  Nessuna struttura disponibile in queste date per la destinazione scelta. Prova a cambiare date o destinazione.
                </CardContent></Card>
              )}

              {/* Timeline */}
              {(plan.days || []).some((d) => d.items.length > 0) && (
                <section className="space-y-3" data-testid="trip-timeline">
                  <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><Sparkles className="h-5 w-5 text-primary" />Il tuo programma giorno per giorno</h2>
                  <div className="space-y-4">
                    {plan.days.map((day) => (
                      <div key={day.date} className="relative rounded-2xl border border-border bg-card p-4" data-testid={`trip-day-${day.day_index}`}>
                        <div className="mb-3 flex items-center gap-2">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/12 text-sm font-bold text-primary">{day.day_index + 1}</span>
                          <div>
                            <p className="text-sm font-semibold leading-none">{day.label}</p>
                            <p className="text-xs text-muted-foreground">{fmtD(day.date)}</p>
                          </div>
                          {day.items.length >= 2 && (
                            <Badge variant="secondary" className="ml-auto gap-1 text-[10px]" data-testid={`trip-day-full-${day.day_index}`}>
                              <Clock className="h-3 w-3" />Giornata piena
                            </Badge>
                          )}
                        </div>
                        {day.items.length === 0 ? (
                          <p className="pl-10 text-sm text-muted-foreground">{day.day_index === 0 ? 'Arrivo e check-in, relax.' : 'Giornata libera — aggiungi un\u2019esperienza!'}</p>
                        ) : (
                          <div className="grid gap-3 pl-0 sm:grid-cols-2 sm:pl-10">
                            {day.items.map((exp) => (
                              <div key={exp.catalog_id} className="flex gap-3 rounded-xl border border-border bg-background p-2.5" data-testid={`trip-exp-${exp.id}`}>
                                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
                                  {exp.image ? <img src={mediaUrl(exp.image)} alt={exp.title} className="h-full w-full object-cover" loading="lazy" />
                                    : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><Sparkles className="h-5 w-5" /></div>}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <Badge variant="secondary" className="text-[10px]">{exp.category}</Badge>
                                  <p className="mt-0.5 line-clamp-1 text-sm font-semibold" title={exp.title}>{exp.title}</p>
                                  <p className="line-clamp-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                                    {exp.location_name && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{exp.location_name}</span>}
                                    {exp.duration_min > 0 && <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />{exp.duration_min}′</span>}
                                  </p>
                                  <div className="mt-1 flex items-center justify-between">
                                    <span className="text-sm font-bold tabular-nums">{eur(exp.price)}</span>
                                    <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" disabled={busy === exp.catalog_id} onClick={() => addExp(exp)} data-testid={`trip-add-exp-${exp.id}`}>
                                      {busy === exp.catalog_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}Aggiungi
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Google Earth 3D discovery */}
              <TripEarthMap stay={selectedStay} experiences={allExps} />

              {/* Add all */}
              <div className="flex flex-col items-stretch gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-display text-lg font-semibold">Pronto a partire?</p>
                  <p className="text-sm text-muted-foreground">
                    Aggiungi l'intero itinerario al tuo viaggio con un tocco.
                    {bundleEligible && <span className="font-medium text-primary"> Risparmi {eur(bundleDiscount)} con il pacchetto!</span>}
                  </p>
                </div>
                <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                  <span className="hidden font-display text-xl font-bold tabular-nums sm:block">{eur(totalAfter)}</span>
                  <Button size="lg" variant="outline" className="gap-2 rounded-full" onClick={saveShare} disabled={saving} data-testid="trip-save-share">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                    Salva e condividi
                  </Button>
                  <Button size="lg" className="gap-2 rounded-full" onClick={addAll} disabled={addingAll} data-testid="trip-add-all">
                    {addingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                    Aggiungi tutto al viaggio
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Google Earth discovery */}
          <Link to="/parchi" data-testid="trip-google-earth" className="group relative block overflow-hidden rounded-2xl border border-border">
            <div className="absolute inset-0 -z-10 bg-[linear-gradient(120deg,hsl(var(--brand-bark))_0%,hsl(var(--brand-lake))_100%)] opacity-95" />
            <div className="flex items-center gap-4 p-5 text-white">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 backdrop-blur"><Globe className="h-6 w-6" /></span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg font-semibold">Esplora la Sila in 3D con Google Earth</p>
                <p className="text-sm text-white/85">Sorvola parchi, laghi e borghi in modalità fotorealistica prima di scegliere.</p>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        </div>

        {/* RIGHT: Il tuo viaggio (carrello attuale) */}
        <aside className="lg:sticky lg:top-6 lg:h-fit">
          <Card className="overflow-hidden" data-testid="trip-current">
            <div className="border-b border-border bg-muted/40 px-5 py-4">
              <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><ShoppingCart className="h-5 w-5 text-primary" />Il tuo viaggio</h2>
            </div>
            <CardContent className="space-y-3 p-5">
              {!isCustomer ? (
                <div className="space-y-3 text-center">
                  <p className="text-sm text-muted-foreground">Accedi come cliente per comporre e salvare il tuo viaggio.</p>
                  <Button className="w-full" onClick={() => navigate('/cliente/accedi')} data-testid="trip-login">Accedi / Registrati</Button>
                </div>
              ) : cartCount === 0 ? (
                <p className="text-sm text-muted-foreground">Il tuo viaggio è vuoto. Genera un itinerario e aggiungi le voci qui.</p>
              ) : (
                <>
                  <div className="space-y-2" data-testid="trip-current-items">
                    {cartItems.slice(0, 6).map((it) => (
                      <div key={it.item_id} className="flex items-center gap-2 text-sm">
                        <span className="line-clamp-1 flex-1">{it.title}</span>
                        <span className="tabular-nums text-muted-foreground">{eur(it.price)}</span>
                      </div>
                    ))}
                    {cartItems.length > 6 && <p className="text-xs text-muted-foreground">+{cartItems.length - 6} altre voci</p>}
                  </div>
                  <Separator />
                  <div className="flex items-end justify-between">
                    <span className="text-sm font-medium">Totale</span>
                    <span className="font-display text-2xl font-bold tabular-nums" data-testid="trip-current-total">{eur(preview?.total)}</span>
                  </div>
                  <Button className="w-full gap-2" size="lg" onClick={() => navigate('/cliente/carrello')} data-testid="trip-checkout">
                    Vai al checkout <ArrowRight className="h-4 w-4" />
                  </Button>
                </>
              )}
              <div className="flex items-center justify-center gap-1.5 pt-1 text-[11px] text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" /> Prezzi verificati lato server
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4" data-testid="trip-why">
            <CardContent className="space-y-2.5 p-5">
              <p className="font-display text-sm font-semibold">Perché comporre qui</p>
              {[
                { icon: Star, t: 'Un solo checkout per soggiorno, esperienze e prodotti' },
                { icon: RefreshCw, t: 'Disponibilità reale e prezzi sempre aggiornati' },
                { icon: Leaf, t: 'Operatori locali e Progetto Sila incluso' },
              ].map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <r.icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{r.t}
                </div>
              ))}
            </CardContent>
          </Card>
        </aside>
      </div>
    </PublicLayout>
  );
}
